import { Loader, Table, Pagination } from '@mantine/core';
import { useState } from 'react';
// components
import { TProjectImage, TRegistryItemData } from '@newo/api/gateway';
import NoRecords from '@/components/NoRecords';
// utils
import { useRegistryItemCreateAndPublishMutation } from '@/pages/VersionControl/VersionControl.queries';
import { useProjectImageDeleteMutation } from '@/services/designer/project_images/project_images.queries';
import { useStorageImagesQuery } from '@/services/bff/bff.queries';
import ProjectImage from '@/pages/VersionControl/components/Storages/components/ProjectImage';
import { openConfirmDeleteModal } from '@/utils/ui';
import { cn } from '@/utils';

interface ProjectImagesListProps {
    storageId: string;
    className?: string;
    isAdmin: boolean;
    searchTerm: string;
}

const PER_PAGE = 25;

const ProjectImagesList = ({
    storageId,
    isAdmin,
    className,
    searchTerm
}: ProjectImagesListProps) => {
    const [page, setPage] = useState(1);
    const { data, isSuccess, isLoading, refetch } = useStorageImagesQuery(
        storageId,
        { page, per: PER_PAGE, query: searchTerm }
    );

    const { mutateAsync: createAndPublishRegistryItem } =
        useRegistryItemCreateAndPublishMutation();
    const { mutateAsync: deleteProjectImage } = useProjectImageDeleteMutation();

    const images = data?.items ?? [];
    const total = data?.metadata.total ?? 0;

    const handleRelease = async (
        registryId: string,
        formData: TRegistryItemData
    ) => {
        return createAndPublishRegistryItem({
            data: formData,
            registryId
        });
    };

    const handleProjectImageDelete = async (projectImage: TProjectImage) => {
        openConfirmDeleteModal({
            title: `Delete ${projectImage.idn} project image?`,
            children: (
                <>
                    Are you sure you want to delete {projectImage.idn}
                    project image?
                </>
            ),
            confirmLabel: 'Delete Image',
            onConfirm: () => {
                deleteProjectImage(
                    {
                        storageId,
                        projectImageId: projectImage.id
                    },
                    {
                        onSuccess: () => {
                            refetch();
                        }
                    }
                );
            }
        });
    };

    return (
        <>
            {isLoading ? (
                <div className="flex justify-center items-center py-8">
                    <Loader size={24} />
                </div>
            ) : undefined}
            {isSuccess && images.length === 0 ? (
                <NoRecords className="py-8" />
            ) : undefined}

            {isSuccess && images.length > 0 ? (
                <div
                    className={cn(
                        'rounded-lg border border-solid border-gray-3',
                        className
                    )}
                >
                    <Table highlightOnHover>
                        <Table.Thead>
                            <Table.Tr>
                                <Table.Th>Idn</Table.Th>
                                <Table.Th className="w-2xs">
                                    Image hash
                                </Table.Th>
                                <Table.Th className="w-2xs">
                                    Created at
                                </Table.Th>
                                <Table.Th className="w-2xs" />
                            </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody className="[&>tr:last-child]:border-b-0!">
                            {images.map((item) => (
                                <ProjectImage
                                    image={item}
                                    isAdmin={isAdmin}
                                    key={item.id}
                                    onDelete={handleProjectImageDelete}
                                    onRelease={handleRelease}
                                />
                            ))}
                        </Table.Tbody>
                    </Table>
                    {total > PER_PAGE && (
                        <div className="flex justify-center py-4">
                            <Pagination
                                total={Math.ceil(total / PER_PAGE)}
                                value={page}
                                onChange={setPage}
                            />
                        </div>
                    )}
                </div>
            ) : undefined}
        </>
    );
};

export default ProjectImagesList;
